import { useMemo, useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import { Sparkles, RotateCcw, Ticket } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Notice } from "@/components/ui/notice";
import EmptyState from "@/components/EmptyState";
import ProgressBar from "@/components/shared/ProgressBar";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import {
  PrizeCard, HandedOutStat, WaitingPill, StudentLink, HandOutRow, HandOutAllButton, HandedOutList,
} from "@/components/schools/prizes/PrizePieces";
import { timeAgo, ordinal } from "@/lib/timeAgo";
import { formatPrizeAmount, topInClassLabel, topStudentWhere, topClassroomMeasure, topClassroomWhere, isAverageRanking } from "@/lib/prizes";
import { formatDate, formatNumber, percentOf, pluralize } from "@/lib/format";
import { formatRoom, formatGrade } from "@/lib/classroom";

/**
 * The school's prize report: for each student prize, who's earned it and
 * who's still waiting, classroom by classroom. Prize draws are run from
 * here once reading ends; top-student and top-classroom winners
 * appear when the campaign finishes.
 */
export default function Disbursements({ school, membership, campaign, prizes, student_milestones_path, milestones_path }) {
  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign.name}
      title="Prize report"
      back={{ href: student_milestones_path, label: "Student prizes" }}
    >
      <Head title={`Prize report · ${campaign.name}`} />

      {prizes.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No prizes to hand out yet"
          description="Add student prizes or a Top classroom school reward first. Winners show up here to hand out."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild><Link href={student_milestones_path}>Add student prizes</Link></Button>
              <Button variant="outline" asChild><Link href={milestones_path}>School rewards</Link></Button>
            </div>
          }
        />
      ) : (
        <div className="space-y-6">
          {prizes.map((p) => {
            if (p.award_style === "raffle") return <DrawPrize key={p.id} prize={p} campaign={campaign} />;
            if (p.award_style === "top_in_class") return <TopOfClassPrize key={p.id} prize={p} campaign={campaign} />;
            if (p.award_style === "top_classroom") return <TopClassroomPrize key={p.id} prize={p} campaign={campaign} />;
            return <UnlockPrize key={p.id} prize={p} />;
          })}
        </div>
      )}
    </SchoolLayout>
  );
}

// ── Everyone who reaches it ─────────────────────────────────

function UnlockPrize({ prize }) {
  const pct = percentOf(prize.disbursed_count, prize.achieved_count);

  return (
    <PrizeCard prize={prize} stat={<HandedOutStat handedOut={prize.disbursed_count} earned={prize.achieved_count} />}>
      {prize.achieved_count > 0 && (
        <ProgressBar pct={pct} footnote={`${pct}% handed out`} barColor="bg-primary" footnoteColor="text-primary" />
      )}
      {prize.classrooms.length === 0 ? (
        <p className="text-sm text-muted-foreground">No students have earned this prize yet.</p>
      ) : (
        <div className="space-y-3">
          {prize.classrooms.map((c) => <ClassroomBlock key={c.classroom_id} classroom={c} prizeName={prize.prize} />)}
        </div>
      )}
    </PrizeCard>
  );
}

function ClassroomBlock({ classroom, prizeName }) {
  const waiting = classroom.pending.length;

  return (
    <div className={`overflow-hidden rounded-lg border ${waiting === 0 ? "border-success/30" : "border-border"}`}>
      <div className="flex flex-col gap-2 bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            <Link href={classroom.classroom_path} className="hover:text-primary">{formatRoom(classroom.classroom_name)}</Link>
            {classroom.grade && <span className="font-normal text-muted-foreground"> · {formatGrade(classroom.grade)}</span>}
          </p>
          <p className="text-xs text-muted-foreground">{classroom.disbursed_count} of {pluralize(classroom.achieved_count, "student")} handed their prize</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WaitingPill count={waiting} />
          <HandOutAllButton path={classroom.disburse_all_path} count={waiting} prizeName={prizeName} />
        </div>
      </div>
      {waiting > 0 && (
        <ul className="divide-y divide-border/60 border-t border-border">
          {classroom.pending.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prizeName} />)}
        </ul>
      )}
      <HandedOutList achievements={classroom.handed_out?.map((a) => ({ ...a, disbursed: true }))} prizeName={prizeName} />
    </div>
  );
}

// ── Prize draw ──────────────────────────────────────────────

function DrawPrize({ prize, campaign }) {
  const eligible = useMemo(() => prize.classroom_breakdown.filter((c) => c.eligible_entries > 0), [prize.classroom_breakdown]);
  const [drawEarly, setDrawEarly] = useState(false);
  // Before reading ends, drawing is a deliberate "Draw early".
  const open = campaign.reading_ended || drawEarly;

  return (
    <PrizeCard
      prize={prize}
      stat={prize.total_entries > 0 && (
        <>
          <p className="font-display text-2xl font-semibold leading-none font-tabular">{formatNumber(prize.total_entries)}</p>
          <p className="mt-1 text-xs text-muted-foreground">entries · {pluralize(prize.entrants_count, "student")}</p>
        </>
      )}
    >
      {prize.draws.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Winners</p>
          <ul className="divide-y divide-border/60 rounded-lg border border-border">
            {prize.draws.map((d) => <DrawWinner key={d.id} draw={d} prizeName={prize.prize} />)}
          </ul>
        </div>
      )}

      {eligible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {prize.draws.length === 0 ? "No students have earned an entry yet." : "Every classroom with entries has had a draw."}
        </p>
      ) : open ? (
        prize.divisions
          ? <DivisionDraws prize={prize} primary={campaign.reading_ended} />
          : <DrawForm prize={prize} classrooms={eligible} primary={campaign.reading_ended} />
      ) : (
        <Notice
          tone="info"
          action={<Button size="sm" variant="outline" onClick={() => setDrawEarly(true)}>Draw early</Button>}
        >
          Draws open when reading ends on {formatDate(campaign.end_on, { weekday: true })}. Entries can still grow until then.
        </Notice>
      )}
    </PrizeCard>
  );
}

function DrawWinner({ draw, prizeName }) {
  const confirm = useConfirm();
  const drawnFrom = draw.division ?? draw.classroom_names.map(formatRoom).join(", ");
  const redraw = async () => {
    const ok = await confirm({
      title: "Clear this draw?",
      body: `${draw.winner_name} is no longer the winner, and ${drawnFrom} can be drawn from again.`,
      confirmLabel: "Clear the draw",
      destructive: true,
    });
    if (ok) router.delete(draw.undo_path, { preserveScroll: true });
  };

  return (
    <HandOutRow
      achievement={{
        student_name: `${draw.winner_name} · ${formatRoom(draw.winner_classroom_name)}`,
        student_path: draw.winner_path,
        disbursed: draw.disbursed,
        disburse_path: draw.disburse_path,
        undisburse_path: draw.undisburse_path,
        certificate_path: draw.certificate_path,
      }}
      prizeName={prizeName}
      detail={`Drawn ${timeAgo(draw.drawn_at)} from ${drawnFrom} · ${pluralize(draw.winner_entries, "entry", "entries")}`}
      extraActions={!draw.disbursed && (
        <Button size="sm" variant="ghost" onClick={redraw} className="text-muted-foreground">
          <RotateCcw className="h-4 w-4" />
          Redraw
        </Button>
      )}
    />
  );
}

function DrawForm({ prize, classrooms, primary }) {
  const confirm = useConfirm();
  const [selected, setSelected] = useState(() => new Set(classrooms.map((c) => c.classroom_id)));
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const entries = classrooms.filter((c) => selected.has(c.classroom_id)).reduce((sum, c) => sum + c.eligible_entries, 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || selected.size === 0) return;
    const ok = await confirm({
      title: `Draw a winner for ${prize.prize}?`,
      body: primary
        ? `One winner, picked at random from ${formatNumber(entries)} entries in the classrooms you chose.`
        : "Reading hasn't ended, so students can still earn entries. Drawing now picks from the entries so far.",
      confirmLabel: "Draw a winner",
    });
    if (!ok) return;
    setSubmitting(true);
    router.post(prize.draw_path, { classroom_ids: Array.from(selected) }, { preserveScroll: true, onFinish: () => setSubmitting(false) });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <fieldset>
        <legend className="text-sm font-medium text-foreground">Draw a winner</legend>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">
          Choose the classrooms in this draw. For a winner per grade band, leave only those classrooms ticked, then run another draw for the rest.
        </p>
        <ul className="space-y-1">
          {classrooms.map((c) => (
            <li key={c.classroom_id}>
              <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-card">
                <Checkbox checked={selected.has(c.classroom_id)} onChange={() => toggle(c.classroom_id)} />
                <span className="flex-1 text-sm text-foreground">
                  {formatRoom(c.classroom_name)}
                  {c.grade && <span className="text-muted-foreground"> · {formatGrade(c.grade)}</span>}
                </span>
                <span className="text-xs text-muted-foreground font-tabular">
                  {pluralize(c.eligible_entries, "entry", "entries")} · {pluralize(c.eligible_entrants, "student")}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant={primary ? "default" : "outline"} disabled={submitting || selected.size === 0 || entries === 0}>
          <Ticket className="h-4 w-4" />
          {submitting ? "Drawing…" : "Draw a winner"}
        </Button>
        <p className="text-xs text-muted-foreground">{pluralize(entries, "entry", "entries")} in this draw</p>
      </div>
    </form>
  );
}

// One "Draw a winner" per division, for a draw run in each division.
function DivisionDraws({ prize, primary }) {
  const confirm = useConfirm();
  const [drawing, setDrawing] = useState(null);

  const draw = async (division) => {
    const ok = await confirm({
      title: `Draw a winner in ${division.label}?`,
      body: primary
        ? `One winner for ${prize.prize}, picked at random from ${formatNumber(division.eligible_entries)} entries in ${division.label}.`
        : "Reading hasn't ended, so students can still earn entries. Drawing now picks from the entries so far.",
      confirmLabel: "Draw a winner",
    });
    if (!ok) return;
    setDrawing(division.key);
    router.post(prize.draw_path, { division: division.key }, { preserveScroll: true, onFinish: () => setDrawing(null) });
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <p className="text-sm font-medium text-foreground">Draw a winner in each division</p>
      <p className="mb-3 mt-1 text-xs text-muted-foreground">Each draw picks from one division's entries. Set each classroom's division on its Edit page.</p>
      <ul className="divide-y divide-border/60">
        {prize.divisions.map((d) => (
          <li key={d.key} className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2">
            <div className="min-w-0">
              <p className="text-sm text-foreground">
                {d.label}
                {d.winners_count > 0 && <span className="text-muted-foreground"> · {pluralize(d.winners_count, "winner")} drawn</span>}
              </p>
              <p className="text-xs text-muted-foreground font-tabular">
                {pluralize(d.classroom_count, "class", "classes")} · {pluralize(d.eligible_entries, "entry", "entries")} · {pluralize(d.eligible_entrants, "student")}
              </p>
            </div>
            {d.eligible_entries > 0 && (
              <Button
                size="sm"
                variant={primary && d.winners_count === 0 ? "default" : "outline"}
                disabled={drawing !== null}
                onClick={() => draw(d)}
              >
                <Ticket className="h-4 w-4" />
                {drawing === d.key ? "Drawing…" : d.winners_count > 0 ? "Draw another" : "Draw a winner"}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Top student ─────────────────────────────────────────────

function TopOfClassPrize({ prize, campaign }) {
  const who = `${topInClassLabel(prize.kind).toLowerCase()} ${topStudentWhere(prize)}`;

  return (
    <PrizeCard
      prize={prize}
      description={prize.finalized
        ? `The ${who} won. Ties all win.`
        : `Goes to the ${who}. The leaders below can still change until ${campaign.name} finishes.`}
      stat={prize.finalized && <HandedOutStat handedOut={prize.disbursed_count} earned={prize.achieved_count} />}
    >
      <ul className="divide-y divide-border/60 rounded-lg border border-border">
        {prize.groups.map((c) => (
          <li key={c.key} className="px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              {c.classroom_path
                ? <Link href={c.classroom_path} className="hover:text-primary">{formatRoom(c.label)}</Link>
                : c.label}
              {c.grade && <span className="font-normal text-muted-foreground"> · {formatGrade(c.grade)}</span>}
            </p>
            {prize.finalized ? (
              c.winners.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">No one qualified.</p>
              ) : (
                <ul className="-mx-4 mt-1">
                  {c.winners.map((w) => <HandOutRow key={w.id} achievement={w} prizeName={prize.prize} detail={null} />)}
                </ul>
              )
            ) : c.leaders.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">No one has qualified yet.</p>
            ) : (
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <p className="text-sm text-foreground">
                  {c.leaders.map((l, i) => (
                    <span key={l.id}>{i > 0 && ", "}<StudentLink name={l.student_name} href={l.student_path} /></span>
                  ))}
                  {c.leaders.length > 1 && <span className="text-muted-foreground"> (tied)</span>}
                </p>
                <span className="shrink-0 text-sm text-muted-foreground font-tabular">{formatPrizeAmount(prize.kind, c.leader_amount)}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </PrizeCard>
  );
}

// ── Top classroom ───────────────────────────────────────────

function TopClassroomPrize({ prize, campaign }) {
  const measure = `${topClassroomMeasure(prize).toLowerCase()} ${topClassroomWhere(prize)}`;
  const amount = (n) => `${formatPrizeAmount(prize.kind, n)}${isAverageRanking(prize.ranked_by) ? " per student" : ""}`;
  const winRow = (win, classroom) => (
    <HandOutRow
      key={win.id}
      achievement={{ ...win, student_name: formatRoom(classroom.classroom_name), student_path: classroom.classroom_path }}
      prizeName={prize.prize}
      detail={classroom.amount != null ? `Won with ${amount(classroom.amount)}` : null}
    />
  );

  return (
    <PrizeCard
      prize={prize}
      description={prize.finalized
        ? `The class with the ${measure} won. Ties all win.`
        : `Goes to the class with the ${measure}. The standings can still change until ${campaign.name} finishes.`}
      stat={prize.finalized && <HandedOutStat handedOut={prize.disbursed_count} earned={prize.achieved_count} />}
    >
      {prize.divisions.length === 0 && <p className="text-sm text-muted-foreground">No classrooms have students yet.</p>}
      {prize.divisions.map((d) => (
        <div key={d.label}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.label}</p>
          <ul className="divide-y divide-border/60 rounded-lg border border-border">
            {d.classrooms.map((c) => (c.win ? winRow(c.win, c) : (
              <li key={c.classroom_id} className="flex items-baseline justify-between gap-3 px-4 py-2.5 text-sm">
                <p className="min-w-0 text-foreground">
                  {/* A class below the minimum has no place to speak of. */}
                  <span className="inline-block w-9 text-muted-foreground font-tabular">{c.qualified ? ordinal(c.rank) : "–"}</span>
                  <Link href={c.classroom_path} className="hover:text-primary">{formatRoom(c.classroom_name)}</Link>
                  {c.grade && <span className="text-muted-foreground"> · {formatGrade(c.grade)}</span>}
                  {c.leading && <span className="font-medium text-primary"> · {prize.finalized ? "won" : "leading"}</span>}
                </p>
                <span className={`shrink-0 font-tabular ${c.qualified ? "text-muted-foreground" : "text-muted-foreground/70"}`}>
                  {amount(c.amount)}{!c.qualified && " · below the minimum"}
                </span>
              </li>
            )))}
          </ul>
        </div>
      ))}
      {prize.other_winners.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Other winners</p>
          <ul className="divide-y divide-border/60 rounded-lg border border-border">
            {prize.other_winners.map((w) => winRow(w, { classroom_name: w.classroom_name }))}
          </ul>
        </div>
      )}
    </PrizeCard>
  );
}
